import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { GetAgents } from '../../store/agents.actions';
import { IAgent, IAgentFilter, IAgentFilterRequest } from '../../models/agents.models';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import * as AgentSelectors from '../../store/agents.selectors';
import * as AgentActions from '../../store/agents.actions';
import { FormControl } from '@angular/forms';
import { DATE_FORMAT } from 'src/app/config/global.config';
import { Actions, ofType } from '@ngrx/effects';
import { AgentService } from '../../services/agent.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { TranslateService } from '@ngx-translate/core';
import { encodeFilterObject, parseFilterRequest, refillFilter } from 'src/app/shared/utils/helpers';
import { AgentsFilterComponent } from '../agents-filter/agents-filter.component';

@Component({
  selector: 'app-agents-listing',
  templateUrl: './agents-listing.component.html',
  styleUrls: ['./agents-listing.component.scss'],
})
export class AgentsListingComponent implements OnInit {
  agents$: Observable<IAgent[]> = this.store.select(AgentSelectors.selectAgents);
  agents: IAgent[] = [];
  filteredAgents: IAgent[] = [];
  selectedAgents: number[] = [];
  filter: IAgentFilterRequest | undefined = undefined;

  searchInput = new FormControl('');

  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;

  DATE_FORMAT = DATE_FORMAT;

  destroy$ = new Subject<void>();

  subscriptions: Subscription[] = [];
  unparsedFilter:IAgentFilter | null = null;
  module:string = 'agent';
  filters: any = {};
  key: string | undefined = undefined;

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private agentService: AgentService,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      this.key = params['keyword'] ?? undefined
      this.searchInput.setValue(this.key ?? '')
      this.filters.keyword = this.key
      if(params['filter'])
      {
        const decodedFilterParam = decodeURIComponent(params['filter']);
        try {
          const parsedFilterParam = JSON.parse(decodedFilterParam);
          this.filters.advanced = refillFilter(this.module, parsedFilterParam);
          this.filter = parseFilterRequest(this.module, this.filters.advanced);
        } catch (error) {
          this.filter = undefined
          this.filters.advanced = refillFilter(this.module, null)
        }
        this.store.dispatch(AgentActions.SetAgentFilter(this.filters))
      }
      else
        {
        this.filter = undefined
        this.filters.advanced = refillFilter(this.module, null)
        this.store.dispatch(AgentActions.SetAgentFilter(this.filters))
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const sub1 = this.store
      .select((state) => state.agents.agentFilterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((filter) => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.store.dispatch(GetAgents({
          filter: this.filter!,
          key: this.key
        }));
      });

    this.agents$
      .pipe(
        filter((agents) => agents.length >= 0),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((agents: IAgent[]) => {
        this.agents = agents;
        this.filteredAgents = [...agents];
      });

    this.actions$
      .pipe(ofType(AgentActions.DeleteAgentsSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedAgents = [];
      });

      this.searchInput.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.search(value);
      });
    this.subscriptions.push(sub1);
  }

  search(value:string | null) {
    this.key = value?.toLowerCase();
    this.filters.keyword = this.key;
    this.store.dispatch(AgentActions.SetAgentFilter(this.filters))
    let queryParams:any = {}
    if(this.key != ''){
      queryParams.keyword = this.key;
    }
    else{
      queryParams.keyword = null;
    }
    this.router.navigate([],{
      queryParams: {...queryParams},
      queryParamsHandling: 'merge'
    })
  }

  // Check if all agents in all companies are selected
  isAllAgentsSelected(): boolean {
    const allAgentIds = this.filteredAgents.map((agent: IAgent) => agent.id);
    return allAgentIds.length
      ? allAgentIds.every((id: any) => this.selectedAgents.includes(id))
      : false;
  }

  // Check if at least one agent is selected but not all
  isPartialSelectedAll(): boolean {
    const allAgentIds = this.filteredAgents.map((agent: IAgent) => agent.id);
    const selectedCount = allAgentIds.filter((id: any) =>
      this.selectedAgents.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < allAgentIds.length;
  }

  toggleAllAgents(checked: boolean) {
    const allIds = this.filteredAgents.map((l: IAgent) => l.id!);
    this.selectedAgents = checked ? [...allIds] : [];
  }

  toggleAgent(agentId: number, checked: boolean) {
    if (checked) {
      this.selectedAgents.push(agentId);
    } else {
      this.selectedAgents = this.selectedAgents.filter((id) => id !== agentId);
    }
  }

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    if (key === this.activeSortKey && sortDirection === this.activeSortMode) return;
    if (!this.filteredAgents || !Array.isArray(this.filteredAgents)) return;

    const isEmpty = (v: any) => v === null || v === undefined || v === '';

    const isStrictDateString = (val: string): boolean => {
      // Matches YYYY-MM-DD, YYYY/MM/DD, with optional time (space or T separator)
      return /^\d{4}[-/]\d{2}[-/]\d{2}([ T]\d{2}:\d{2}:\d{2})?$/.test(val);
    }

    const compareValues = (a: any, b: any): number => {
      if (isEmpty(a) && isEmpty(b)) return 0;
      if (isEmpty(a)) return 1;
      if (isEmpty(b)) return -1;

      const isDateA = typeof a === 'string' && isStrictDateString(a);
      const isDateB = typeof b === 'string' && isStrictDateString(b);

      if (isDateA || isDateB) {
        return new Date(a).getTime() - new Date(b).getTime();
      }

      if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
      }

      return String(a).localeCompare(String(b));
    };



    this.filteredAgents = [...this.filteredAgents].sort((a: any, b: any) => {
      let valA: any;
      let valB: any;

      if (key === 'fullname') {
        valA = `${a.first_name ?? ''} ${a.last_name ?? ''}`
          .trim()
          .toLowerCase();
        valB = `${b.first_name ?? ''} ${b.last_name ?? ''}`
          .trim()
          .toLowerCase();
      } else {
        valA = a[key];
        valB = b[key];
      }

      const result = compareValues(valA, valB);
      return sortDirection === 'asc' ? result : -result;
    });

    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
  }

  trackByAgentId(_index: number, agent: IAgent): string | number {
    return agent.id!;
  }

  goToDetails(agent: IAgent) {
    this.router.navigate(['/agents/', agent.id]);
  }

  download() {
    this.agentService.exportAgents(this.filteredAgents);
  }

  deleteSelectedAgents() {
    this.store.dispatch(
      AgentActions.DeleteAgents({ ids: this.selectedAgents })
    );
  }

deleteConfirmation() {
  // 1. Find the FIRST agent that has completed orders among those selected
  const agentWithOrders = this.agents
    .filter(agent => this.selectedAgents.includes(agent.id!))
    .find(agent => agent.order_count && agent.order_count > 0);

  // 2. If such an agent exists, show the "Not Allowed" popup for just that one agent
  if (agentWithOrders) {
    const agentName = `${agentWithOrders.first_name} ${agentWithOrders.last_name}`;
    this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: 'assets/icones/cancel_alert.svg',
        title: this.translateService.instant('common.action_not_allowed'),
        text: this.translateService.instant(
          'common.delete_not_allowed_completed_orders'
        ) + ` (${agentName})`,
        confirm_text: this.translateService.instant('common.ok'),
        hideCancel: true,
      },
    });
    return;
  }

  const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
    data: {
      icon: 'assets/icones/cancel_alert.svg',
      confirm_text: this.translateService.instant('common.remove'),
    },
  });
  
  dialogRef.afterClosed().subscribe((result: boolean) => {
    if (result) {
      this.deleteSelectedAgents();
    }
  });
}

  newAgent() {
    this.router.navigate(['/agents/add']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFilter() {
    const dialogRef = this.dialog.open(AgentsFilterComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe((filterResult: any) => {
      if (filterResult) {
        const unparsedFilter = filterResult;
        const encoded = encodeFilterObject(unparsedFilter);
        this.router.navigate([], {
          queryParams: {
            //page: 1,
            filter: encoded,
          },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  hasModuleFilters(): boolean {
    if (this.filter) return this.filter.filters && this.filter?.filters?.length > 0;
    return false;
  }

  clearFilter() {
    this.filter = undefined;
    this.filters.advanced = refillFilter(this.module, null);
    this.store.dispatch(AgentActions.SetAgentFilter(this.filters));
    this.router.navigate([], {});
  }
}
